import React from 'react';
import AppsIcon from '@mui/icons-material/Apps';
import {
  ClickAwayListener,
  Divider,
  Grow,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuList,
  Paper,
  Popper,
  Typography,
} from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import UserApps from '~/config/UserApps';
import dashboardIcon from '~/assets/app_dashboard.svg?url';
import useAppMenu from '~/hooks/useAppMenu';

export default function AppMenu() {
  const { open, anchorElement, handleClick, handleClose, handleAppClick } = useAppMenu();

  return (
    <>
      <Box
        className="app-container"
        sx={{ display: 'flex', alignItems: 'center', textAlign: 'center' }}>
        <Tooltip title="App Menu">
          <IconButton
            onClick={handleClick}
            size="big"
            sx={{ ml: 2, p: 0 }}
            aria-controls={open ? 'app-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}>
            <AppsIcon
              className="appDrawerIcon"
              style={{ color: '#f5f5f5' }}
              fontSize="large"
            />
          </IconButton>
        </Tooltip>
      </Box>
      <Popper
        open={open}
        anchorEl={anchorElement}
        role={undefined}
        transition
        placement="bottom-end"
        disablePortal
        sx={theme => ({ zIndex: theme.zIndex.modal + 1 })}>
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{ transformOrigin: placement === 'bottom' ? 'right top' : 'right bottom' }}>
            <Paper
              className="appDrawer"
              elevation={4}
              sx={{
                mt: 1,
                overflow: 'hidden',
                width: 350,
                borderRadius: 3,
              }}>
              <ClickAwayListener onClickAway={handleClose}>
                <Box sx={{ px: 1, py: 1 }}>
                  <List
                    dense
                    sx={{ py: 0 }}>
                    <ListItem disablePadding>
                      <ListItemButton
                        sx={{
                          color: 'primary.main',
                          gap: 2,
                        }}>
                        <ListItemIcon
                          sx={{
                            alignItems: 'center',
                            width: 40,
                            height: 40,
                            justifyContent: 'center',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                          }}>
                          <Box
                            alt="Dashboard"
                            component="img"
                            src={dashboardIcon}
                            sx={{
                              height: 28,
                              width: 28,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary="Dashboard"
                          slotProps={{
                            primary: {
                              color: 'primary.main',
                              variant: 'subtitle1',
                            },
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                    <Divider sx={{ my: 1 }} />
                  </List>

                  <Typography
                    className="appDrawerTitle"
                    sx={{ mt: 1 }}>
                    {UserApps.title}
                  </Typography>
                  <MenuList
                    autoFocusItem={open}
                    id="app-menu"
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, 1fr)',
                      py: 0.5,
                    }}>
                    {UserApps.apps.map(app => (
                      <IconButton
                        className="iconPadding"
                        key={app.id}
                        onClick={e => handleAppClick(e, app.url, `secure-ad-server-app-1`)}>
                        <Box>
                          <Box
                            sx={{
                              alignItems: 'center',
                              display: 'flex',
                              height: 32,
                              justifyContent: 'center',
                            }}>
                            {app.icon}
                          </Box>
                          <Typography
                            className="appTitle"
                            variant="body2">
                            {app.title}
                          </Typography>
                        </Box>
                      </IconButton>
                    ))}
                  </MenuList>
                  <Divider sx={{ my: 1 }} />
                  <List
                    dense
                    sx={{ py: 0.2 }}>
                    {UserApps.products.map((product, index) => (
                      <React.Fragment key={product.id}>
                        <ListItem disablePadding>
                          <ListItemButton
                            className="appProductItem"
                            onClick={e => handleAppClick(e, product.url, product.windowName)}
                            sx={{
                              color: 'grey.600',
                              py: 2,
                              my: 1,
                              px: 0.75,
                              '&:hover': {
                                color: '#084384',
                                '& img': { filter: 'none' },
                                '& .MuiListItemText-primary': { color: '#084384' },
                                '& .MuiListItemText-secondary': { color: '#084384' },
                              },
                            }}>
                            <ListItemIcon
                              sx={{
                                width: 40,
                                height: 40,
                                color: 'inherit',
                                border: '1px solid #e0e0e0',
                                borderRadius: 1,
                                boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: 0.5,
                                mr: 1.5,
                              }}>
                              {product.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={product.title}
                              secondary={product.subtitle}
                              slotProps={{
                                primary: {
                                  fontSize: 13,
                                  fontWeight: 500,
                                },
                                secondary: {
                                  fontSize: 11,
                                },
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                        {index < UserApps.products.length - 1 && <Divider sx={{ mx: 0.5 }} />}
                      </React.Fragment>
                    ))}
                  </List>
                </Box>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
}
